import React, { ReactElement } from "react";
import { DISMISS, NewNotification } from "collections/Notifications";
import { Notification } from "store/notification";
import { Exchange, OperationResult } from "@urql/core";
import { TokenExpired } from "auth";
import { pipe, tap } from "wonka";
import GraphqlErrorNotification from "./GraphqlErrorNotification";
import { AllSelectionRemovedError } from "./featureExchange";
import { LocalKeys } from "utilities/i18n";

interface notifyContextBase {
  dismiss?: Notification["dismiss"];
}

interface notifyContextWithMessage extends notifyContextBase {
  __kind: "message";
  message: string | ReactElement;
}

interface notifyContextWithKey extends notifyContextBase {
  __kind: "key";
  i18nKey: LocalKeys;
}

interface notifyContextWithHandler extends notifyContextBase {
  __kind: "handler";
  handler: (result: OperationResult) => {};
}

interface PolyContext {
  operation: {
    context: {
      notify?:
        | notifyContextWithKey
        | notifyContextWithMessage
        | notifyContextWithHandler;
    };
  };
}

export const noOpNotifyContext = {
  notify: {
    handler: () => {},
    __kind: "handler"
  }
};

export const noLogoutOnAuthErrorContext = {
  notify: {
    handler: () => {},
    __kind: "handler"
  },
  noLogout: true
};

/**
 * Creating an instance of CombinedError will invoke a heavy string operation to create the message.
 * Lets use a type for the "in the middle" as we generate a smaller error stack for logging to DD.
 *
 * Permits passing in "notify" arguments on the context.  All arguments are optional:
 * 1. A handler function.  This could be a no-op, or an actual function, depending on your need.
 *    if provided, this function will be used instead of the built-in notification handler.
 * 2. An i18n key to use in the notification.  Can support the sprintf insertion of the error if desired.
 *    The i18n translation should use {{error}} in the string to capture the automatically-passed-in
 *    error object.
 * 3. A message string.  If this is passed in, be sure it has been translated prior!
 * 4. A dismiss option (see the dismiss options in the store/notification.ts type)
 *
 * If no arguments are provided, renders the default "Could not load data" notification on error.
 *
 */
export const notifyExchange = (
  notify: React.Dispatch<NewNotification>
): Exchange => {
  const handNotification = (result: OperationResult & PolyContext) => {
    const notifyOpts = result.operation.context?.notify;
    if ("handler" === notifyOpts?.__kind) {
      const notificationHandler = notifyOpts.handler;

      if (typeof notificationHandler === "function") {
        notificationHandler(result);
        return;
      }
    }

    const { error } = result;

    if (!error) {
      return;
    }
    //Remove noise from potentially run out feature flags.
    if (
      error.graphQLErrors.length >= 1 &&
      error.graphQLErrors.every(({ originalError }) =>
        AllSelectionRemovedError.isType(originalError)
      )
    ) {
      return;
    }
    const { response } = error;

    if (!response || response.status !== 401) {
      let message: string | ReactElement = React.createElement(
        GraphqlErrorNotification,
        {
          error
        }
      );

      const dismiss = notifyOpts?.dismiss || DISMISS.SLOW;

      if ("message" === notifyOpts?.__kind) {
        message = notifyOpts.message;
      }

      if ("key" === notifyOpts?.__kind) {
        message = React.createElement(GraphqlErrorNotification, {
          error,
          i18nKey: notifyOpts.i18nKey
        });
      }

      notify({
        id: "useQueryResponseError",
        type: "error",
        message,
        dismiss
      });

      return;
    }

    // This should only ever fire if the response is one of "invalid / expired token"
    notify({
      id: "token_invalid",
      message: React.createElement(TokenExpired),
      type: "error",
      dismiss: "NOT_DISMISSIBLE"
    });
  };

  return ({ forward }) => {
    return ops$ => pipe(ops$, forward, tap(handNotification));
  };
};
